import React, { useMemo, useState } from 'react';
import { confirmDialog } from '../lib/confirm';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../../config/axios';
import { Meta, Display, Chip, Icons } from '../components/Atoms';
import Layout, { useViewport } from '../components/Layout';
import { LoadingState, ErrorState, EmptyState } from '../components/states';
import { useApi } from '../lib/api';
import { notifyError, notifySuccess } from '../lib/notify';

/**
 * Photo Gallery - asymetryczny grid pod nagłówkami miesięcy.
 * Z paczki photo.jsx (ScreenGallery) zaadaptowane do prawdziwych danych.
 *
 * Endpoint:
 *   /api/gallery - wszystkie zdjęcia konta zalogowanego z metadanymi
 *
 * Layout:
 *   - Mobile: kolumna z asymetrycznymi grupami fotek (big/2-col/3-col rotacja)
 *   - Tablet/Desktop: szerszy grid 3-4 kolumny + sidebar z filtrami
 */

const MONTHS_PL = ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'];

// ── Helpers ──────────────────────────────────────────────────────
function imageUrl(path) {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  if (path.startsWith('/')) return path;
  return `/${path}`;
}

function groupByMonth(photos) {
  const grouped = new Map();
  photos.forEach(p => {
    const dateStr = p.taken_date || p.created_at;
    if (!dateStr) return;
    const d = new Date(dateStr);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (!grouped.has(key)) {
      grouped.set(key, {
        year: d.getFullYear(),
        month: d.getMonth(),
        photos: [],
      });
    }
    grouped.get(key).photos.push(p);
  });
  // Sortuj malejąco (najnowsze pierwsze) i wewnątrz grupy też malejąco
  const result = Array.from(grouped.values()).sort((a, b) =>
    b.year - a.year || b.month - a.month
  );
  result.forEach(g => g.photos.sort((a, b) => {
    const dateA = new Date(a.taken_date || a.created_at);
    const dateB = new Date(b.taken_date || b.created_at);
    return dateB - dateA;
  }));
  return result;
}

function tagFor(photo) {
  // Tag based on bed/plant context
  if (photo.tag && photo.tag !== 'ogólne') return photo.tag;
  if (photo.source_type === 'progress') return 'Postęp';
  if (photo.source_type === 'harvest') return 'Zbiór';
  if (photo.source_type === 'quick') return null;
  return null;
}

function dayLabel(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// ── Photo tile ───────────────────────────────────────────────────
function PhotoTile({ photo, height = 160, onClick, selectMode, selected, onToggleSelect }) {
  const tag = tagFor(photo);
  const src = imageUrl(photo.thumb_path || photo.medium_path || photo.photo_path);
  const dateLabel = dayLabel(photo.taken_date || photo.created_at);

  const handleClick = (e) => {
    if (selectMode) {
      e.preventDefault();
      e.stopPropagation();
      onToggleSelect?.(photo.id);
    } else if (onClick) {
      onClick();
    }
  };

  return (
    <div
      onClick={handleClick}
      style={{
        position: 'relative',
        height,
        borderRadius: 10,
        overflow: 'hidden',
        background: 'linear-gradient(135deg, var(--t-sage), var(--t-amber))',
        cursor: 'pointer',
        transition: 'transform 250ms ease',
        outline: selected ? '3px solid var(--t-sage)' : 'none',
        outlineOffset: selected ? -3 : 0,
        opacity: selectMode && !selected ? 0.8 : 1,
      }}
      onMouseEnter={e => { if (!selectMode) e.currentTarget.style.transform = 'scale(1.02)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
    >
      {src && (
        // Natywny lazy-loading — miniatury doładowują się dopiero przy wejściu w viewport
        // (mniej danych na LTE, szybszy pierwszy render dużych galerii).
        <img
          src={src}
          alt={tag || dateLabel || 'Zdjęcie z galerii'}
          loading="lazy"
          decoding="async"
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      )}
      {selectMode && (
        <div style={{
          position: 'absolute', top: 8, right: 8, zIndex: 2,
          width: 26, height: 26, borderRadius: '50%',
          background: selected ? 'var(--t-sage)' : 'rgba(0,0,0,0.5)',
          border: `2px solid ${selected ? 'var(--t-sage)' : 'rgba(255,255,255,0.7)'}`,
          display: 'grid', placeItems: 'center',
          color: selected ? 'var(--t-bg)' : 'transparent',
          fontWeight: 700, fontSize: 14,
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
        }}>{selected ? '✓' : ''}</div>
      )}
      {!src && (
        // Fallback ilustracja gdy brak fotki
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.35 }}
             viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
          <path d="M50 90V40M50 40C50 25 35 15 20 15c0 18 15 30 30 25zM50 40c0-12 12-22 28-22 0 12-12 22-28 22z"
                fill="#000" opacity=".4" />
        </svg>
      )}
      {dateLabel && (
        <div style={{
          position: 'absolute', bottom: 8, left: 10,
          fontFamily: 'var(--t-mono)', fontSize: 10,
          letterSpacing: '.18em', textTransform: 'uppercase',
          color: 'var(--t-text)',
          background: 'rgba(0, 0, 0, 0.55)',
          padding: '3px 7px',
          borderRadius: 4, fontWeight: 500,
        }}>{dateLabel}</div>
      )}
      {tag && (
        <div style={{
          position: 'absolute', top: 8, left: 10,
          fontFamily: 'var(--t-mono)', fontSize: 8,
          letterSpacing: '.18em', textTransform: 'uppercase',
          color: 'var(--t-bg)',
          background: 'var(--t-sage)',
          padding: '3px 7px', borderRadius: 4, fontWeight: 600,
        }}>{tag}</div>
      )}
    </div>
  );
}

// ── Asymmetric layout dla grupy miesięcznej ──────────────────────
function MonthGrid({ photos, onPick, mobile, selectMode, selectedIds, onToggleSelect }) {
  if (photos.length === 0) return null;
  const tileProps = (p) => ({
    selectMode,
    selected: selectedIds?.has(p.id),
    onToggleSelect,
  });

  // Strategia mobile: hero + 2-col + 3-col rotacja
  // Strategia desktop: grid auto-fill 3-4 kolumny
  if (mobile) {
    const groups = [];
    let i = 0;
    while (i < photos.length) {
      // hero (1 big)
      if (i < photos.length) {
        groups.push({ kind: 'hero', items: [photos[i]] });
        i += 1;
      }
      // 2-col
      if (i < photos.length) {
        const items = photos.slice(i, i + 2);
        groups.push({ kind: 'pair', items });
        i += items.length;
      }
      // 3-col small
      if (i < photos.length) {
        const items = photos.slice(i, i + 3);
        groups.push({ kind: 'triple', items });
        i += items.length;
      }
    }

    return (
      <>
        {groups.map((g, idx) => (
          <div key={idx} style={{ marginTop: idx === 0 ? 0 : 6 }}>
            {g.kind === 'hero' && (
              <PhotoTile photo={g.items[0]} height={240} onClick={() => onPick(g.items[0])} {...tileProps(g.items[0])} />
            )}
            {g.kind === 'pair' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {g.items.map(p => (
                  <PhotoTile key={p.id} photo={p} height={160} onClick={() => onPick(p)} {...tileProps(p)} />
                ))}
              </div>
            )}
            {g.kind === 'triple' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6 }}>
                {g.items.map(p => (
                  <PhotoTile key={p.id} photo={p} height={110} onClick={() => onPick(p)} {...tileProps(p)} />
                ))}
              </div>
            )}
          </div>
        ))}
      </>
    );
  }

  // Desktop: równe kafle 4-kolumnowe
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
      gap: 10,
    }}>
      {photos.map(p => (
        <PhotoTile key={p.id} photo={p} height={180} onClick={() => onPick(p)} {...tileProps(p)} />
      ))}
    </div>
  );
}

// ── Główny content ──────────────────────────────────────────────
function GalleryContent({ photos, mobile, navigate, selectMode, selectedIds, onToggleSelect }) {
  const { t } = useTranslation();
  const grouped = useMemo(() => groupByMonth(photos), [photos]);
  const totalPhotos = photos.length;

  if (totalPhotos === 0) {
    return (
      <EmptyState
        illustration="bed"
        title="Galeria czeka na pierwszy kadr"
        description="Zdjęcie ułożone obok daty mówi więcej niż notatka. Sięgnij po aparat."
      />
    );
  }

  return (
    <>
      <div style={{ padding: mobile ? '0 24px 18px' : '0 0 24px' }}>
        <Meta color="sage" style={{ marginBottom: 10 }}>
          Galeria · {totalPhotos} {totalPhotos === 1 ? 'zdjęcie' : totalPhotos < 5 ? 'zdjęcia' : 'zdjęć'}
        </Meta>
        <Display size={mobile ? 42 : 56} style={{
          display: 'block', lineHeight: 0.95, letterSpacing: '-.025em',
        }}>
          Od nasiona<br />
          do <span style={{ fontStyle: 'italic', color: 'var(--t-sage)' }}>kwiatu</span>.
        </Display>
      </div>

      {/* Filter chips */}
      <div style={{
        padding: mobile ? '0 24px 18px' : '0 0 24px',
        display: 'flex', gap: 8, overflowX: 'auto',
      }}>
        <Chip selected>Wszystkie</Chip>
        <Chip>Osiągnięcia</Chip>
        <Chip tone="sage">Zdrowie</Chip>
        <Chip tone="rust">Problemy</Chip>
      </div>

      {grouped.map(g => (
        <div key={`${g.year}-${g.month}`} style={{ marginBottom: 32 }}>
          <div style={{
            padding: mobile ? '0 24px 12px' : '0 0 14px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          }}>
            <Display size={24}>{MONTHS_PL[g.month]} {g.year}</Display>
            <Meta>{g.photos.length} {g.photos.length === 1 ? 'zdjęcie' : g.photos.length < 5 ? 'zdjęcia' : 'zdjęć'}</Meta>
          </div>
          <div style={{ padding: mobile ? '0 24px' : '0' }}>
            <MonthGrid
              photos={g.photos}
              mobile={mobile}
              onPick={p => navigate(`/gallery/${p.id}`)}
              selectMode={selectMode}
              selectedIds={selectedIds}
              onToggleSelect={onToggleSelect}
            />
          </div>
        </div>
      ))}
    </>
  );
}

// ── Floating Action Button (camera) ──────────────────────────────
function CameraFAB({ onClick }) {
  return (
    <button
      onClick={onClick}
      aria-label="Otwórz aparat"
      style={{
        position: 'fixed',
        bottom: 'calc(80px + env(safe-area-inset-bottom, 0))',
        right: 24, zIndex: 45,
        width: 56, height: 56, borderRadius: '50%',
        background: 'var(--t-sage)', color: 'var(--t-bg)',
        border: 'none', cursor: 'pointer',
        display: 'grid', placeItems: 'center',
        boxShadow: '0 8px 24px rgba(168, 196, 158, 0.3)',
        transition: 'transform 200ms ease',
      }}
      onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.94)'; }}
      onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
    >
      <svg width="24" height="24" viewBox="0 0 16 16" fill="none">
        <rect x="1" y="4" width="14" height="10" rx="1.5" stroke="var(--t-bg)" strokeWidth="1.6" />
        <circle cx="8" cy="9" r="2.5" stroke="var(--t-bg)" strokeWidth="1.6" />
        <path d="M5 4l1-2h4l1 2" stroke="var(--t-bg)" strokeWidth="1.6" />
      </svg>
    </button>
  );
}

// ── Mobile content ──────────────────────────────────────────────
function MobileContent({ photos, navigate, selectMode, selectedIds, onToggleSelect }) {
  return (
    <div className="t-stagger" style={{ maxWidth: 480, margin: '0 auto' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '20px 20px 18px',
      }}>
        <Link to="/plants" style={{ textDecoration: 'none' }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'var(--t-bg-elev)', border: '1px solid var(--t-line)',
            display: 'grid', placeItems: 'center',
          }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M12 4l-6 6 6 6" stroke="var(--t-text)" strokeWidth="1.6"
                    strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </Link>
        <Meta>Galeria</Meta>
        <div style={{ width: 36 }} />
      </div>
      <GalleryContent
        photos={photos} navigate={navigate} mobile
        selectMode={selectMode} selectedIds={selectedIds} onToggleSelect={onToggleSelect}
      />
    </div>
  );
}

// ── Desktop content ─────────────────────────────────────────────
function DesktopContent({ photos, navigate, selectMode, selectedIds, onToggleSelect }) {
  return (
    <div className="t-stagger" style={{ padding: '32px 40px 60px' }}>
      <GalleryContent
        photos={photos} navigate={navigate} mobile={false}
        selectMode={selectMode} selectedIds={selectedIds} onToggleSelect={onToggleSelect}
      />
    </div>
  );
}

// ── Gallery screen root ──────────────────────────────────────────
export default function Gallery() {
  const { t } = useTranslation();
  const { isPhone } = useViewport();
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useApi('/api/gallery');

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  if (error && (error.status === 401 || error.message?.includes('autoryz'))) {
    return <Layout active="plants"><ErrorState error={{ status: 401, message: error.message }} /></Layout>;
  }
  if (loading) return <Layout active="plants"><LoadingState label={t('common.loading')} /></Layout>;

  const photos = Array.isArray(data) ? data : [];

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const enterSelectMode = () => {
    setSelectMode(true);
    setSelectedIds(new Set());
  };
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const selectAll = () => {
    setSelectedIds(new Set(photos.map(p => p.id)));
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!await confirmDialog(`Usunąć ${selectedIds.size} ${selectedIds.size === 1 ? 'zdjęcie' : selectedIds.size < 5 ? 'zdjęcia' : 'zdjęć'}? Operacja nieodwracalna.`)) return;
    setDeleting(true);
    try {
      await axios.delete('/api/gallery/bulk', { data: { photoIds: Array.from(selectedIds) } });
      notifySuccess(`Usunięto ${selectedIds.size} ${selectedIds.size === 1 ? 'zdjęcie' : selectedIds.size < 5 ? 'zdjęcia' : 'zdjęć'}.`);
      exitSelectMode();
      refetch?.();
    } catch (err) {
      notifyError('Nie udało się usunąć: ' + (err.response?.data?.error || err.message || ''));
    } finally {
      setDeleting(false);
    }
  };

  const Content = isPhone ? MobileContent : DesktopContent;

  return (
    <Layout active="plants">
      <Content
        photos={photos}
        navigate={navigate}
        selectMode={selectMode}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
      />

      {/* Toolbar selekcji — sticky bottom */}
      {selectMode ? (
        <div style={{
          position: 'fixed',
          left: 12, right: 12,
          bottom: 'calc(80px + env(safe-area-inset-bottom, 12px))',
          zIndex: 50,
          background: 'var(--t-bg-elev)',
          border: '1px solid var(--t-line-2)',
          borderRadius: 14,
          padding: '12px 14px',
          display: 'flex', alignItems: 'center', gap: 12,
          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.45)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          maxWidth: 'min(640px, calc(100vw - 24px))',
          margin: '0 auto',
        }}>
          <div style={{ flex: 1, color: 'var(--t-text)', fontSize: 14 }}>
            <strong style={{ color: 'var(--t-sage)' }}>{selectedIds.size}</strong>
            <span style={{ color: 'var(--t-text-dim)' }}> / {photos.length} wybrane</span>
          </div>
          <button
            type="button"
            onClick={selectAll}
            disabled={selectedIds.size === photos.length}
            style={toolbarBtn('ghost')}
          >Zaznacz wszystkie</button>
          <button
            type="button"
            onClick={handleBulkDelete}
            disabled={selectedIds.size === 0 || deleting}
            style={{
              ...toolbarBtn('danger'),
              opacity: selectedIds.size === 0 || deleting ? 0.4 : 1,
              cursor: selectedIds.size === 0 || deleting ? 'not-allowed' : 'pointer',
            }}
          >{deleting ? 'Usuwam…' : `Usuń (${selectedIds.size})`}</button>
          <button
            type="button"
            onClick={exitSelectMode}
            aria-label="Zakończ wybór"
            style={{
              ...toolbarBtn('ghost'),
              padding: '8px 10px',
            }}
          >×</button>
        </div>
      ) : (
        photos.length > 0 && (
          <button
            type="button"
            onClick={enterSelectMode}
            style={{
              position: 'fixed',
              left: 24, top: 'max(16px, env(safe-area-inset-top, 16px))',
              zIndex: 45,
              background: 'rgba(15, 20, 16, 0.85)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid var(--t-line-2)',
              borderRadius: 999,
              padding: '8px 14px',
              color: 'var(--t-text-dim)',
              fontFamily: 'var(--t-mono)', fontSize: 10,
              letterSpacing: '.18em', textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >Wybierz</button>
        )
      )}

      {!selectMode && <CameraFAB onClick={() => navigate('/gallery/capture')} />}
    </Layout>
  );
}

const toolbarBtn = (variant) => {
  const base = {
    padding: '8px 14px', borderRadius: 8,
    fontFamily: 'var(--t-sans)', fontSize: 13, fontWeight: 500,
    cursor: 'pointer', letterSpacing: '-.01em',
  };
  if (variant === 'danger') {
    return {
      ...base,
      background: 'var(--t-rust)',
      color: 'var(--t-bg)',
      border: 'none',
    };
  }
  return {
    ...base,
    background: 'transparent',
    color: 'var(--t-text-dim)',
    border: '1px solid var(--t-line-2)',
  };
};
